import { DisplayTreeNode, DisplayTreeNodeType } from "./types";

export const ROOT_PARENT_KEY = "root";

// Create an optimized search function that's more efficient for large trees
export const findSearchHits = <T, S>(
  tree: DisplayTreeNode[],
  searchInput: T,
  objByIds: Record<string, S>,
  matches: (obj: S, searchInput: T, context?: any) => boolean,
  context?: any,
) => {
  // Use Sets for faster lookups and to avoid duplicates
  const parentsOfMatches = new Set<string>();
  const searchHits = new Set<string>();

  // Create an iterative approach instead of recursive for better performance
  const processNodes = (nodes: DisplayTreeNode[]) => {
    // Use a stack to avoid deep recursion
    const stack: DisplayTreeNode[] = [...nodes];

    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) {
        continue;
      }

      // Skip folder nodes immediately
      if (node.nodeType === DisplayTreeNodeType.Folder) {
        // Still process its children
        if (node.children && node.children.length > 0) {
          stack.push(...node.children);
        }
        continue;
      }

      // Check if this node matches
      const obj: S | undefined = objByIds[node.key];
      const isSearchHit = obj && matches(obj, searchInput, context);

      if (isSearchHit) {
        searchHits.add(node.key);

        // Add all ancestors to the parents set
        if (node.parentKey !== ROOT_PARENT_KEY && node.ancestorKeys) {
          node.ancestorKeys.forEach((key) => parentsOfMatches.add(key));
        }
      }

      // Add children to the stack if they exist
      if (node.children && node.children.length > 0) {
        stack.push(...node.children);
      }
    }
  };

  // Start the search
  processNodes(tree);

  return {
    keys: Array.from(searchHits),
    parentKeys: Array.from(parentsOfMatches),
  };
};
